import React, { useEffect, useState } from 'react'
import { Button, Offcanvas } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { z } from 'zod'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { ISelectOption } from 'types/IClassifier'
import { TraderRequest, TraderResponse } from '../models/types'

interface TraderEditPanelProps {
  trader: TraderResponse | null
  isSaving: boolean
  onCancel: () => void
  onSave: (request: TraderRequest) => void
}

const SECURITY_CODES_WITH_SECURITY = '1 \u00b7 3'
const SECURITY_CODES_WITHOUT_SECURITY = '0 \u00b7 2'

const blankRequest: TraderRequest = {
  name: '',
  identificationNumber: '',
  typeOfPerson: null,
  addressLine: '',
  street: '',
  streetNumber: '',
  city: '',
  country: '',
  postcode: '',
  contactPersonName: '',
  contactPersonEmail: '',
  contactPersonTelephone: '',
}

function toRequest(trader: TraderResponse): TraderRequest {
  return {
    name: trader.name,
    identificationNumber: trader.identificationNumber,
    typeOfPerson: trader.typeOfPerson,
    addressLine: trader.address?.addressLine ?? '',
    street: trader.address?.street ?? '',
    streetNumber: trader.address?.streetNumber ?? '',
    city: trader.address?.city ?? '',
    country: trader.address?.country ?? '',
    postcode: trader.address?.postcode ?? '',
    contactPersonName: trader.contactPerson?.name ?? '',
    contactPersonEmail: trader.contactPerson?.email ?? '',
    contactPersonTelephone: trader.contactPerson?.telephone ?? '',
  }
}

function TraderEditPanel(props: Readonly<TraderEditPanelProps>) {
  const {
    trader, isSaving, onCancel, onSave,
  } = props
  const { t } = useTranslation()
  const [, countryOptions] = useCodelist('COUNTRIES')
  const [values, setValues] = useState<TraderRequest>(blankRequest)

  useEffect(() => {
    if (trader) {
      setValues(toRequest(trader))
    }
  }, [trader])

  const field = (key: keyof TraderRequest, label: string, className = 'col-md-6') => (
    <div className={`${className} mb-3`} key={key}>
      <label className="form-field__label" htmlFor={`trader-${key}`}>{label}</label>
      <input
        id={`trader-${key}`}
        className="form-control"
        value={values[key] ?? ''}
        onChange={(event) => setValues({ ...values, [key]: event.target.value })}
      />
    </div>
  )

  const hasIdentity = Boolean(values.name?.trim() || values.identificationNumber?.trim())
  const hasValidEmail = !values.contactPersonEmail?.trim()
    || z.string().email().safeParse(values.contactPersonEmail).success

  return (
    <Offcanvas
      show={trader !== null}
      onHide={onCancel}
      placement="end"
      className="trader-edit-panel"
    >
      <Offcanvas.Header className="border-bottom py-2" closeButton>
        <Offcanvas.Title as="h2" className="fs-4 fw-normal mb-0 text-dark">
          {t('traders.editTitle')}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="trader-edit-panel__fields">
          <div className="row trader-edit-panel__row">
            {field('name', t('traders.name'))}
            {field('identificationNumber', t('traders.identificationNumber'))}
          </div>
          <fieldset className="trader-edit-panel__group">
            <legend className="trader-edit-panel__group-heading">
              <span>{t('traders.address')}</span>
              <span className="trader-edit-panel__group-heading-rule" />
            </legend>
            <div className="row trader-edit-panel__row">
              <div className="col-md-4 mb-3">
                <span className="form-field__label" id="trader-country-label">{t('traders.country')}</span>
                <Select
                  className="select"
                  classNamePrefix="select"
                  aria-labelledby="trader-country-label"
                  isClearable
                  options={countryOptions}
                  value={countryOptions.find((option: ISelectOption) => option.value === values.country) ?? null}
                  onChange={(option) => setValues({ ...values, country: (option as ISelectOption)?.value ?? null })}
                />
              </div>
              {field('postcode', t('traders.postcode'), 'col-md-4')}
              {field('city', t('traders.city'), 'col-md-4')}
            </div>
            <div className="trader-edit-panel__address-forms">
              <fieldset className="trader-edit-panel__address-form">
                <legend className="trader-edit-panel__address-form-caption">
                  {t('traders.addressWithSecurity')}
                  <span className="trader-edit-panel__security-codes">{SECURITY_CODES_WITH_SECURITY}</span>
                </legend>
                <div className="row">
                  {field('street', t('traders.street'), 'col-8')}
                  {field('streetNumber', t('traders.streetNumber'), 'col-4')}
                </div>
              </fieldset>
              <fieldset className="trader-edit-panel__address-form">
                <legend className="trader-edit-panel__address-form-caption">
                  {t('traders.addressWithoutSecurity')}
                  <span className="trader-edit-panel__security-codes">{SECURITY_CODES_WITHOUT_SECURITY}</span>
                </legend>
                <div className="row">
                  {field('addressLine', t('traders.addressLine'), 'col-12')}
                </div>
              </fieldset>
            </div>
          </fieldset>
          <fieldset className="trader-edit-panel__group">
            <legend className="trader-edit-panel__group-heading">
              <span>{t('traders.contactPerson')}</span>
              <span className="trader-edit-panel__group-heading-rule" />
            </legend>
            <div className="row trader-edit-panel__row">
              {field('contactPersonName', t('traders.name'), 'col-md-4')}
              {field('contactPersonEmail', t('traders.email'), 'col-md-4')}
              {field('contactPersonTelephone', t('traders.telephone'), 'col-md-4')}
            </div>
          </fieldset>
        </div>
        {trader !== null && !hasIdentity && (
          <p className="text-danger small mb-0">{t('traders.identityRequired')}</p>
        )}
        {trader !== null && !hasValidEmail && (
          <p className="text-danger small mb-0">{t('traders.invalidEmail')}</p>
        )}
        <div className="trader-edit-panel__footer">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>{t('buttons.cancel')}</Button>
          <Button variant="primary" onClick={() => onSave(values)} disabled={isSaving || !hasIdentity || !hasValidEmail}>
            {t('buttons.save')}
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  )
}

export default TraderEditPanel
